// packages/studio/web/src/editor/useStudioWorkspace.ts
//
// The Studio ↔ WorkspaceStore glue. Studio's editor adopts the shared
// app-shell WorkspaceStore (so it can drive the shared Editor / EditorTabs /
// WorkspaceNav primitives) by modelling the changeset as:
//
//   base    = committed trunk content   (pristine — never advanced by autosave)
//   working = committed + this changeset's drafts
//
// With base pinned to committed, the store's own `dirtyPaths` (working ≠ base)
// is exactly Studio's "modified"/"new" set — for free. The source is READONLY
// (you never write trunk directly; you Propose), so the store's auto-write-back
// binder stays off and we own draft persistence here instead: every store file
// mutation event is debounced into the proposal-scoped drafts API. Editing is
// still allowed (readonly only means "no write-back to the source").
import { useEffect, useRef, useState } from "react";
import {
  createWorkspaceStore,
  type WorkspaceStore,
} from "@archlang/app-shell/workbench";
import { saveDraft, deleteDraft, markDeleted } from "../proposals/proposalsApi.js";

/** The resolved working tree for a changeset (route's `vfs-seed` query). */
export interface StudioVfsData {
  readonly committed: Map<string, string>;
  readonly working: Map<string, string>;
  readonly draftPaths: ReadonlySet<string>;
  readonly tombstones: ReadonlySet<string>;
}

export interface StudioWorkspaceArgs {
  readonly vfsData: StudioVfsData | undefined;
  /** Active changeset id (drafts are scoped to it). Null → no persistence. */
  readonly csId: string | null;
  readonly slug: string;
  readonly repo: string;
  /** File to open (URL path), or "" / null for none. */
  readonly activePath: string | null;
  /** Called when a draft autosave/delete fails (network, 402 quota, 409). Lets
   *  the host surface the failure instead of silently losing the edit. */
  readonly onSaveError?: (err: unknown, path: string) => void;
}

const DRAFT_DEBOUNCE_MS = 700;

/** Composite op key — persistence ops are scoped per (changeset, path).
 *  NUL cannot appear in a path, so the key is collision-free. */
const opKey = (cs: string, p: string): string => `${cs}\u0000${p}`;

/**
 * Build a WorkspaceStore that mirrors the changeset, and persist its edits to
 * the drafts API. Re-hydrates (base + working) whenever the changeset's resolved
 * vfs changes. The store always exists (so callers can use store hooks
 * unconditionally); `ready` flips true once the first vfs has hydrated.
 */
/** Imperative control over the pending debounced draft saves — for host flows
 *  that must sequence against them: Update/Propose FLUSH first (commit what
 *  the user sees), Revert CANCELS first (a timer surviving into the revert's
 *  network round-trips would resurrect the reverted draft). */
export interface DraftPersistence {
  /** Fire every pending debounced save NOW and resolve once the server has
   *  acknowledged them — INCLUDING ops already in flight (fired debounces,
   *  immediate deletes/tombstones/renames). REJECTS when any op failed (it
   *  also surfaces via onSaveError), so a caller sequencing a commit after
   *  the flush must not proceed on stale draft rows. */
  flushAll(): Promise<void>;
  /** Drop every pending debounced save without firing it. (Ops already in
   *  flight are HTTP round-trips — they cannot be recalled.) */
  cancelAll(): void;
}

export function useStudioWorkspace(args: StudioWorkspaceArgs): {
  readonly store: WorkspaceStore;
  readonly ready: boolean;
  readonly drafts: DraftPersistence;
} {
  const { vfsData, csId, slug, repo, activePath } = args;
  // Held in a ref so the persistence subscription doesn't re-bind when the
  // callback identity changes.
  const onSaveErrorRef = useRef(args.onSaveError);
  onSaveErrorRef.current = args.onSaveError;

  // One store for the whole editor mount. The store is EDITABLE (Studio is an
  // editor): create/rename/delete flow through the unified nav (storeNavSource
  // gates those on the store's `readonly` flag) and autosave as drafts via the
  // subscription below. "Don't touch trunk" is enforced by the readonly *source*
  // (committed trunk has no `write()`), not by freezing the store — see the
  // WorkspaceStore doc: readonly is a property of the source, not the editor.
  const [store] = useState<WorkspaceStore>(() => createWorkspaceStore({ readonly: false }));

  // Latest committed map + changeset id, read by the persistence subscription
  // without re-subscribing on every change.
  const committedRef = useRef<Map<string, string>>(new Map());
  const csIdRef = useRef<string | null>(csId);
  csIdRef.current = csId;
  // Pending debounced saves, keyed by (changeset, path) — see `opKey`. Each
  // holds its timer, the exact edit it will write (`content` + the changeset
  // it was scheduled in), AND a `flush` that fires the save immediately — so
  // leaving the editor (unmount / pagehide) within the 700ms debounce window
  // persists the edit instead of dropping it (bugloop L2 #1). The changeset
  // in the KEY matters: with a path-only key, an ordinary NEW-changeset edit
  // of the same path clobbered the OLD changeset's failed-save retry (timer +
  // pendingEdits overwritten), permanently losing the switch-flushed edit.
  const timersRef = useRef<
    Map<
      string,
      {
        timer: ReturnType<typeof setTimeout>;
        content: string;
        scheduledId: string;
        flush: () => Promise<boolean>;
      }
    >
  >(new Map());
  // Every persistence op currently in flight (fired saves/deletes/tombstones,
  // debounced or immediate), tagged with the changeset it targets. `flushAll`
  // must await THESE too — a save whose debounce already fired is out of the
  // timers map but not yet acknowledged, and Update/Propose squashing before
  // it lands races the draft rows. The tag lets flushAll reject only on
  // ACTIVE-changeset failures (its contract protects the squash of the
  // changeset being committed — a foreign changeset's permanently-failing
  // retry must not block every other changeset's Update/Propose forever).
  const inflightRef = useRef<Set<Promise<{ ok: boolean; cs: string }>>>(new Set());
  // Every edit not yet ACKNOWLEDGED by the server, keyed by (changeset, path)
  // — spans the debounce window AND the in-flight window AND failure (a
  // failed save re-arms; see `fire`). The warm re-hydrate overlay reads THIS
  // (not the timers map): a refetched snapshot predating an un-acked edit
  // must not visibly revert it, whether its debounce has fired yet or not.
  // `trunk` pins the path's committed content IN ITS OWN changeset at
  // schedule time (undefined = not committed there): a cross-cs retry firing
  // after `committedRef` swapped to the NEW changeset must not re-decide
  // save-vs-deleteDraft against the wrong trunk — with the new trunk equal to
  // the old edit, the live check issued deleteDraft against the OLD
  // changeset's differing row (edit lost, row destroyed).
  const pendingEditsRef = useRef<
    Map<string, { path: string; content: string; scheduledId: string; trunk: string | undefined }>
  >(new Map());
  // Deletions/renames not yet reflected by a refetched snapshot — the
  // structural counterpart of pendingEdits: a warm re-hydrate from a snapshot
  // taken BEFORE a delete/rename must not resurrect the old file (the server
  // already holds the tombstone / the renamed row). Cleared per entry once a
  // snapshot no longer carries the path.
  // `committed` pins whether the path was committed IN ITS OWN changeset at
  // remove time — a retry firing after a changeset switch must not re-decide
  // markDeleted-vs-deleteDraft against the NEW changeset's committed map.
  // Keyed by (changeset, path), like pendingEdits.
  const pendingDeletesRef = useRef<
    Map<string, { path: string; scheduledId: string; committed: boolean }>
  >(new Map());
  // The last (changeset, content) the server ACKED per path — lets flushDraft
  // skip a byte-identical re-save (an edit detour that lands back on already-
  // saved content re-fired an identical request otherwise). A delete for the
  // path clears its entry (the row is gone; identical content must save again).
  const lastAckedRef = useRef<Map<string, { scheduledId: string; content: string }>>(new Map());
  // Per-(changeset, path) op SEQUENCING — every network op for a key chains
  // behind the previous one's settle. Without it, delete-then-recreate fired
  // markDeleted and saveDraft as two concurrent round-trips against the SAME
  // server row: a tombstone landing after the save left server truth diverged
  // from the UI (file shown alive, row tombstoned) with no repair path.
  const opChainRef = useRef<Map<string, { tail: Promise<unknown>; depth: number }>>(new Map());
  // Stable DraftPersistence facade over the live timers map (see the interface
  // doc). Methods close over the refs, so the object identity never changes.
  const [drafts] = useState<DraftPersistence>(() => ({
    flushAll: async () => {
      // Fire pending timers first (their tracked promises join the in-flight
      // set synchronously), then await EVERYTHING in flight. A failed op on
      // the ACTIVE changeset must REJECT — the callers exist to sequence a
      // commit after acknowledged saves; "resolved" on a failed save would
      // let Update/Propose squash stale draft rows and permanently commit
      // without the user's last edit. A FOREIGN changeset's failure (a
      // surviving cross-cs retry) doesn't taint this changeset's squash —
      // it stays on its own retry cadence. `active` is pinned at CALL time:
      // the await spans network round-trips, and a mid-flight changeset
      // switch must not re-classify the very failures this call gates on.
      const active = csIdRef.current;
      for (const e of [...timersRef.current.values()]) void e.flush();
      const results = await Promise.all([...inflightRef.current]);
      if (results.some((r) => !r.ok && r.cs === active)) {
        throw new Error("A draft save failed — the edit is not on the server yet.");
      }
    },
    cancelAll: () => {
      // Scoped to the ACTIVE changeset — the caller (Revert) only rewrites
      // ITS changeset's rows out-of-band. A foreign changeset's surviving
      // retry (the sole carrier of a switch-flushed edit) must keep running.
      const active = csIdRef.current;
      for (const [k, e] of [...timersRef.current.entries()]) {
        if (e.scheduledId === active) {
          clearTimeout(e.timer);
          timersRef.current.delete(k);
        }
      }
      for (const [k, e] of [...pendingEditsRef.current.entries()]) {
        if (e.scheduledId === active) pendingEditsRef.current.delete(k);
      }
      for (const [k, d] of [...pendingDeletesRef.current.entries()]) {
        if (d.scheduledId === active) pendingDeletesRef.current.delete(k);
      }
      // The caller is about to change server truth out-of-band (Revert deletes
      // every draft row directly) — this changeset's recorded acks are now
      // unreliable, and a stale ack would silently SKIP re-saving identical
      // re-typed content.
      for (const [p, a] of [...lastAckedRef.current.entries()]) {
        if (a.scheduledId === active) lastAckedRef.current.delete(p);
      }
    },
  }));

  // (Re)seed base + working when the resolved vfs changes (boot, changeset
  // switch, external refresh — e.g. revert/propose invalidate the vfs-seed
  // query). base = committed so dirty tracks vs trunk. Cancel any in-flight
  // autosave so a stale debounce can't resurrect a just-reverted draft.
  const [ready, setReady] = useState(false);
  // Synchronous "have we hydrated once" flag. `ready` (state) can't gate the
  // cold-vs-preserve decision: several vfs re-hydrates fire back-to-back before
  // React commits setReady(true), so they'd all read the stale `false`. A ref
  // flips immediately, so only the genuine first hydrate seeds tabs cold.
  const hydratedOnce = useRef(false);
  // Which changeset the CURRENT tab set belongs to — tabs carry per-changeset
  // state (a review tab pins its changesetId), so preservation is only valid
  // within one changeset.
  const hydratedCsId = useRef<string | null>(null);
  // A changeset SWITCH makes the current hydration stale: `ready` must drop
  // until the NEW changeset's vfs has seeded, or hosts gating on it (the
  // review auto-open's "wait for the workspace to hydrate") run against the
  // OLD changeset's tab set — and the cold reseed then wipes what they opened.
  useEffect(() => {
    if (hydratedOnce.current && hydratedCsId.current !== csId) setReady(false);
  }, [csId]);
  useEffect(() => {
    if (!vfsData) return;
    // Cold boot (first hydrate) seeds board+file fresh; a SAME-changeset
    // re-hydrate (baseRef resolve, revert/propose refetch) preserves the
    // existing tab list — otherwise it would drop a plugin custom tab (the
    // review tab opened by the `?view=diff`/non-author auto-open, which races
    // this seed). A CHANGESET SWITCH reseeds cold: the old tabs' state pins the
    // previous changeset (a preserved review tab would keep offering the OLD
    // changeset's diff + Accept inside the new one).
    const cold = !hydratedOnce.current || hydratedCsId.current !== csId;
    // Pending debounced saves at a re-hydrate boundary: FLUSH them — each save
    // is scoped to the changeset it was scheduled in, so it lands in the right
    // draft rows even across a changeset switch (the last <700ms of typing
    // before a switcher click must not vanish). And on a SAME-changeset reseed,
    // OVERLAY every not-yet-ACKNOWLEDGED edit (debouncing, in flight, or
    // failed-retrying — the pendingEdits map spans all three; the timers map
    // alone misses a fired save still on the wire) onto the incoming working
    // map: the refetched snapshot predates them, so seeding it verbatim would
    // visibly revert the just-typed text — clean UI hiding a live server draft
    // row. An intentional Revert cancels everything (drafts.cancelAll) before
    // its deletes, so nothing survives into ITS refetch unless the user typed
    // during the round-trip — and such an edit stays, visibly, as a new draft.
    const working = new Map(vfsData.working);
    // A foreign (changeset, path)'s op is still LIVE while a timer holds the
    // key (armed debounce / failed-attempt retry) OR an op is on the chain
    // (fired, mid-round-trip — between debounce-fire and server response
    // there is NO timer). Both are re-arm liveness windows: pruning the
    // entry/marker inside either turns ONE transient failure of the flushed
    // op into a permanent silent loss (the re-arm guards require them).
    const opLive = (k: string): boolean =>
      timersRef.current.has(k) || (opChainRef.current.get(k)?.depth ?? 0) > 0;
    // Structural ops first (a delete marker for a path a later local edit
    // re-created must lose to the edit overlay below).
    for (const [k, d] of pendingDeletesRef.current) {
      if (d.scheduledId !== csId) {
        // Another changeset's marker never overlays here — but while its op
        // is live (see above), the marker is fireDelete's re-arm liveness
        // token. It self-cleans on a later hydrate once the op settles.
        if (!opLive(k)) {
          pendingDeletesRef.current.delete(k);
        }
      } else if (pendingEditsRef.current.has(k)) {
        // A LOCAL edit re-created the path after the delete (delete + create,
        // or a rename round-trip landing back on it) — the edit is newer, the
        // marker is void. Without this the marker never clears (snapshots
        // keep carrying the re-created path) and eventually eats the file.
        pendingDeletesRef.current.delete(k);
      } else if (!vfsData.working.has(d.path)) {
        // The snapshot no longer carries the path — the deletion is reflected.
        pendingDeletesRef.current.delete(k);
      } else if (!cold) {
        working.delete(d.path);
      }
    }
    for (const [k, e] of pendingEditsRef.current) {
      if (e.scheduledId !== csId) {
        // Another changeset's leftover — never overlays here. Keep it ONLY
        // while its op is live (timer armed OR mid-round-trip on the chain):
        // `fire`'s retry re-arm requires the matching entry (same
        // liveness-token logic as the deletes pass above); otherwise drop it.
        if (!opLive(k)) {
          pendingEditsRef.current.delete(k);
        }
      } else if (vfsData.working.get(e.path) === e.content) {
        // The snapshot caught up with the edit — it no longer needs shielding.
        pendingEditsRef.current.delete(k);
      } else if (!cold) {
        working.set(e.path, e.content);
      }
    }
    for (const { flush } of [...timersRef.current.values()]) void flush();
    timersRef.current.clear();
    committedRef.current = new Map(vfsData.committed);
    const isDoc = activePath?.endsWith(".md") ?? false;
    store.hydrate({
      base: vfsData.committed,
      working,
      // .md files open as doc tabs (CM6), not file tabs (Monaco). Pass null
      // here so seedTabs doesn't create a Monaco file tab for the .md path;
      // we open the doc tab explicitly right after.
      activeFile: activePath && activePath !== "" && !isDoc ? activePath : null,
      preserveTabs: !cold,
    });
    hydratedOnce.current = true;
    hydratedCsId.current = csId;
    // Open the URL's .md as a doc tab only on a COLD seed — re-running it on
    // every background refetch (propose/update/revert invalidation) would steal
    // the active tab back to the doc.
    if (cold && isDoc && activePath) {
      store.openTab({ kind: "doc", path: activePath });
    }
    setReady(true);
    // activePath intentionally excluded — opening a file shouldn't reseed the
    // working tree (that flows through setActiveFile instead).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, vfsData]);

  // Persist store mutations to the proposal-scoped drafts API. base is never
  // advanced, so a saved draft still reads "modified" vs committed trunk.
  useEffect(() => {
    const timers = timersRef.current;
    // Flipped by the cleanup — a failed save settling AFTER unmount must not
    // re-arm a retry into a dead editor (an immortal 5s save loop otherwise).
    let disposed = false;
    const committed = () => committedRef.current;
    const wasCommitted = (p: string): boolean => committed().has(p);
    const isDirtyVsTrunk = (p: string, content: string): boolean =>
      !committed().has(p) || content !== committed().get(p);

    // Fire a persistence call and surface any failure to the host — a swallowed
    // rejection here means the user's edit silently didn't save (network / 402
    // quota / 409). Never throw out of the subscription. Every op registers in
    // the in-flight set (until settled) and resolves to its success, so
    // `flushAll` can await acknowledgement AND detect failure.
    // Chain an op behind the previous op for the same (changeset, path) —
    // fires whether the predecessor succeeded or failed, but never overlaps
    // it (see opChainRef). An IDLE chain fires synchronously (callers and
    // tests rely on the request leaving in the same tick).
    const chained = <T,>(k: string, fn: () => Promise<T>): Promise<T> => {
      let entry = opChainRef.current.get(k);
      if (!entry) {
        entry = { tail: Promise.resolve(), depth: 0 };
        opChainRef.current.set(k, entry);
      }
      const e = entry;
      // Promise.resolve-wrap: a test-mocked op may return undefined.
      const run = (): Promise<T> => Promise.resolve(fn());
      const start = e.depth === 0 ? run() : e.tail.then(run, run);
      e.depth += 1;
      const settled = start.then(
        () => undefined,
        () => undefined,
      );
      e.tail = settled;
      void settled.then(() => {
        e.depth -= 1;
      });
      return start;
    };
    const track = (pr: unknown, p: string, cs: string): Promise<boolean> => {
      const settled = Promise.resolve(pr).then(
        () => true,
        (err) => {
          onSaveErrorRef.current?.(err, p);
          return false;
        },
      );
      const tagged = settled.then((ok) => ({ ok, cs }));
      inflightRef.current.add(tagged);
      void tagged.finally(() => inflightRef.current.delete(tagged));
      return settled;
    };

    // `scheduledId` is the changeset the edit was made in, captured at schedule
    // time — the save targets THAT changeset's draft rows explicitly, so it is
    // correct even when the active changeset has moved on by the time it fires
    // (a switcher jump mid-debounce persists to the OLD changeset, never the
    // new one). Reverted edits can't resurrect through here: Revert cancels
    // its timers explicitly before deleting drafts.
    const flushDraft = (p: string, content: string, scheduledId: string): Promise<boolean> => {
      // Dirty-vs-trunk from the PINNED trunk content when the pendingEdits
      // entry carries this exact edit (see the ref doc) — the live
      // committedRef belongs to whatever changeset is active NOW.
      const pinned = pendingEditsRef.current.get(opKey(scheduledId, p));
      const dirty =
        pinned && pinned.content === content
          ? pinned.trunk === undefined || content !== pinned.trunk
          : isDirtyVsTrunk(p, content);
      if (dirty) {
        const k = opKey(scheduledId, p);
        // Idempotence: the server already ACKED this exact (path, cs, content)
        // — a re-save is pure waste. Resolve true so flushAll stays clean.
        // ONLY while no op for this (changeset, path) is unsettled: an
        // in-flight DIFFERENT-content save moves the row after this check,
        // and its ack-time invalidation of the stale ack comes too late for
        // a skip decided here (the final edit was then never sent — server
        // held the mid-detour content under a clean flushAll). With the
        // chain busy, queue; the fire-time re-check below skips if the ack
        // still holds once the predecessor settles.
        const acked = lastAckedRef.current.get(p);
        const idle = (opChainRef.current.get(k)?.depth ?? 0) === 0;
        if (idle && acked && acked.scheduledId === scheduledId && acked.content === content) {
          return Promise.resolve(true);
        }
        const op = chained(k, () => {
          // Re-checked at FIRE time (the op may have queued behind a busy
          // chain): if the pendingEdits entry no longer carries this exact
          // edit — cancelAll dropped it (Revert), a newer edit superseded
          // it, or a refetched snapshot already reflects it — the queued
          // save must NOT go out. A cancelled Revert save firing here
          // re-created a phantom draft row for the reverted content.
          const pe = pendingEditsRef.current.get(k);
          if (!pe || pe.content !== content) return Promise.resolve();
          // No ack-based skip HERE: a queued op can fire one microtask
          // BEFORE the predecessor's ack bookkeeping (the stale-ack
          // invalidation) runs, so a recorded ack is not trustworthy at
          // this point — the worst case of sending anyway is a redundant
          // idempotent re-save.
          return saveDraft(slug, repo, scheduledId, p, content);
        });
        // Chain-tail pin: `lastAcked` must reflect the LAST op the server will
        // process for this key. If anything queued BEHIND this save (a
        // row-destroying deleteDraft/markDeleted whose call-time ack
        // invalidation ran before this ack even existed), recording here
        // would leave a lie standing after the destroyer lands — and the
        // final edit's debounce then ack-skips over a destroyed row.
        const tailAtIssue = opChainRef.current.get(k)?.tail;
        return track(op, p, scheduledId).then((ok) => {
          if (ok) {
            // Record the ack only while this content is still the LATEST
            // un-acked edit for this (changeset, path) — a stale save settling
            // after a newer edit must not mark the newer content's slot as
            // acked — AND only while this op is still the chain tail (see the
            // pin above). A landed save of DIFFERENT content still moved the
            // server row: an older recorded ack for the path is now a lie,
            // and leaving it standing made the dedupe SKIP the newer edit's
            // save entirely (edit Y → Z → Y).
            const pe = pendingEditsRef.current.get(k);
            const isTail = opChainRef.current.get(k)?.tail === tailAtIssue;
            if (isTail && pe && pe.content === content) {
              lastAckedRef.current.set(p, { scheduledId, content });
            } else {
              const acked = lastAckedRef.current.get(p);
              if (acked && acked.scheduledId === scheduledId && acked.content !== content) {
                lastAckedRef.current.delete(p);
              }
            }
          }
          return ok;
        });
      }
      // Edited back to committed → drop the draft row (its absence, not any
      // content, is now the server truth for the path).
      lastAckedRef.current.delete(p);
      const k = opKey(scheduledId, p);
      // Pin the marker (if any) standing when THIS deleteDraft was issued —
      // the ack cleanup below may only clear that one. A marker registered
      // LATER (the user deletes the file while this deleteDraft is on the
      // wire; its markDeleted queues behind us on the chain) is a LIVE
      // deletion whose retry guard needs the marker — wiping it killed the
      // tombstone's retry after a transient failure (deletion silently lost).
      const markerAtIssue = pendingDeletesRef.current.get(k);
      return track(
        chained(k, () => deleteDraft(slug, repo, scheduledId, p)),
        p,
        scheduledId,
      ).then((ok) => {
        // On ack the server truth IS trunk for the path — clear the entry
        // (still matching this content) instead of leaving it to wait for a
        // snapshot-content match that breaks the moment trunk moves: the
        // stale entry then overlaid PRE-update trunk content over every warm
        // hydrate forever (phantom dirty file with no draft row behind it).
        // A delete MARKER standing since BEFORE this op is obsolete for the
        // same reason (the row is gone; deleteDraft chained after it) —
        // without this, a rename A→B→A back onto trunk content left
        // rename-1's marker to strip A from the next warm hydrate.
        if (ok) {
          const pe = pendingEditsRef.current.get(k);
          if (pe && pe.content === content) pendingEditsRef.current.delete(k);
          if (markerAtIssue && pendingDeletesRef.current.get(k) === markerAtIssue) {
            pendingDeletesRef.current.delete(k);
          }
        }
        return ok;
      });
    };
    // On failed autosave, retry on this cadence (each attempt also re-surfaces
    // via onSaveError, keeping the failure visible) until it lands or a newer
    // edit supersedes it.
    const RETRY_MS = 5000;
    // Fire the save; on failure RE-ARM the timer entry — without that, the
    // edit is out of the timers map forever, so a failed-then-settled save
    // would let the next flushAll resolve clean and Update/Propose squash
    // stale rows with no retry path. Re-arm ONLY while this exact content is
    // still the latest un-acked edit for the path (the pendingEdits entry
    // matches): a NEWER edit's save must not be overwritten by a stale
    // retry, a revert's cancelAll (which clears pendingEdits) must stay
    // cancelled, and an unmounted editor (disposed) must not keep a retry
    // loop alive. Success does NOT clear the pendingEdits entry: a refetched
    // snapshot taken BEFORE the save can land AFTER its ack, and the overlay
    // must still shield the edit — the entry clears when a snapshot finally
    // REFLECTS it (see the hydrate effect).
    const fire = (p: string, content: string, scheduledId: string): Promise<boolean> =>
      flushDraft(p, content, scheduledId).then((ok) => {
        const k = opKey(scheduledId, p);
        if (!ok && !disposed && !timers.has(k)) {
          const pe = pendingEditsRef.current.get(k);
          if (pe && pe.content === content) {
            schedule(p, content, scheduledId, RETRY_MS);
          }
        }
        return ok;
      });
    const schedule = (p: string, content: string, scheduledId: string, delay: number): void => {
      const k = opKey(scheduledId, p);
      const existing = timers.get(k);
      if (existing) clearTimeout(existing.timer);
      // `flush` fires this exact edit immediately (idempotent — clears the timer
      // + entry first so a later cleanup/pagehide can't double-send).
      const flush = (): Promise<boolean> => {
        const e = timers.get(k);
        if (!e) return Promise.resolve(true);
        clearTimeout(e.timer);
        timers.delete(k);
        return fire(p, content, scheduledId);
      };
      timers.set(k, {
        timer: setTimeout(() => {
          timers.delete(k);
          void fire(p, content, scheduledId);
        }, delay),
        content,
        scheduledId,
        flush,
      });
    };
    const debounce = (p: string, content: string): void => {
      const scheduledId = csIdRef.current;
      if (!scheduledId) return;
      pendingEditsRef.current.set(opKey(scheduledId, p), {
        path: p,
        content,
        scheduledId,
        trunk: committed().get(p),
      });
      schedule(p, content, scheduledId, DRAFT_DEBOUNCE_MS);
    };
    // Cancel the ACTIVE changeset's pending op for the path — local ops
    // (delete, rename) only ever supersede work in their own changeset; a
    // foreign changeset's armed retry for the same path is untouched.
    const cancel = (p: string): void => {
      const id = csIdRef.current;
      if (!id) return;
      const k = opKey(id, p);
      const e = timers.get(k);
      if (e) clearTimeout(e.timer);
      timers.delete(k);
      pendingEditsRef.current.delete(k);
    };
    // Fire every pending debounced save NOW — used when leaving the editor so an
    // in-window edit isn't dropped.
    const flushAll = (): void => {
      for (const { flush } of [...timers.values()]) void flush();
    };
    // Fire the delete/tombstone op; a FAILURE re-arms it as a retry timer
    // entry (same discipline as `fire` for saves — a settled failure with no
    // timer would let flushAll resolve clean and Update squash a tree where
    // the deletion silently didn't happen). The re-arm is guarded by the
    // pendingDeletes marker still standing (cancelAll clears it; a local
    // re-creation supersedes it via the hydrate pass) and by `disposed`.
    const fireDelete = (p: string, scheduledId: string): Promise<boolean> => {
      const k = opKey(scheduledId, p);
      // A local RE-CREATION of the path (delete + create, rename round-trip)
      // supersedes the delete — checked at FIRE time, not just in the hydrate
      // marker pass: a retry or hydrate-flush landing after the re-created
      // file's save would otherwise PUT a tombstone over the live draft row
      // (markDeleted and saveDraft target the same row) while the UI shows
      // the file alive. (Same-changeset by construction: the key carries it.)
      if (pendingEditsRef.current.has(k)) {
        pendingDeletesRef.current.delete(k);
        return Promise.resolve(true);
      }
      // The delete invalidates any recorded ack for the path — whatever lands
      // there later must save for real.
      lastAckedRef.current.delete(p);
      // Op choice pinned at remove time (marker.committed): after a changeset
      // switch, `wasCommitted` reads the NEW changeset's committed map — a
      // cross-cs retry would pick the wrong op for the OLD changeset's rows.
      const marker = pendingDeletesRef.current.get(k);
      const committedThen = marker ? marker.committed : wasCommitted(p);
      const op = chained(k, () => {
        // Re-checked at FIRE time (the op may have queued behind a busy
        // chain): a marker gone by now means the deletion is no longer wanted
        // — cancelAll dropped it (Revert), a local re-creation superseded it,
        // or a refetched snapshot already reflects it. A cancelled Revert
        // tombstone firing here buried the freshly-reverted row.
        if (!pendingDeletesRef.current.has(k)) return Promise.resolve();
        // A LOCAL RE-CREATION while this delete was QUEUED supersedes it —
        // the call-time check above the chain ran before the re-creation
        // existed, and a tombstone landing now would bury the re-created
        // file's live row (UI shows it alive; Update deletes it from trunk).
        if (pendingEditsRef.current.has(k)) {
          pendingDeletesRef.current.delete(k);
          return Promise.resolve();
        }
        return committedThen
          ? markDeleted(slug, repo, scheduledId, p)
          : deleteDraft(slug, repo, scheduledId, p);
      });
      return track(op, p, scheduledId).then((ok) => {
        if (
          !ok &&
          !disposed &&
          !timers.has(k) &&
          !pendingEditsRef.current.has(k) &&
          pendingDeletesRef.current.has(k)
        ) {
          scheduleDelete(p, scheduledId, RETRY_MS);
        }
        return ok;
      });
    };
    const scheduleDelete = (p: string, scheduledId: string, delay: number): void => {
      const k = opKey(scheduledId, p);
      const existing = timers.get(k);
      if (existing) clearTimeout(existing.timer);
      const flush = (): Promise<boolean> => {
        const e = timers.get(k);
        if (!e) return Promise.resolve(true);
        clearTimeout(e.timer);
        timers.delete(k);
        return fireDelete(p, scheduledId);
      };
      timers.set(k, {
        timer: setTimeout(() => {
          timers.delete(k);
          void fireDelete(p, scheduledId);
        }, delay),
        content: "",
        scheduledId,
        flush,
      });
    };
    // A committed file removed in this changeset becomes a tombstone (deletion
    // git applies on propose/update); a draft-only/new file just drops its row.
    // The removal registers in pendingDeletes so a stale warm re-hydrate can't
    // resurrect the file while the tombstone is on the wire / un-refetched.
    const remove = (p: string): void => {
      cancel(p);
      const id = csIdRef.current;
      if (!id) return;
      pendingDeletesRef.current.set(opKey(id, p), {
        path: p,
        scheduledId: id,
        committed: wasCommitted(p),
      });
      void fireDelete(p, id);
    };

    const off = store.on((ev) => {
      switch (ev.type) {
        case "fileChanged":
        case "fileCreated":
          // Debounced (even a new empty file — an added file is a change), so a
          // create-then-discard never hits the server.
          debounce(ev.path, ev.content);
          break;
        case "fileRemoved":
          remove(ev.path);
          break;
        case "fileRenamed": {
          remove(ev.from); // registers the old path in pendingDeletes too
          cancel(ev.to);
          const id = csIdRef.current;
          if (id) {
            // The new path's content joins pendingEdits so the overlay shields
            // the rename against a pre-rename snapshot (old tree) landing
            // while the save is on the wire — and the save goes through `fire`
            // so a FAILURE re-arms a retry (a bare track here left a settled
            // failure invisible to flushAll: tombstone landed, new row absent,
            // Update would commit the file out of existence).
            pendingEditsRef.current.set(opKey(id, ev.to), {
              path: ev.to,
              content: ev.content,
              scheduledId: id,
              trunk: committed().get(ev.to),
            });
            void fire(ev.to, ev.content, id);
          }
          break;
        }
        // reveal / boardFly / reload / selection — not persistence.
      }
    });

    // A tab close / reload can happen mid-debounce — flush so the edit persists
    // (best-effort; the browser may not await it, but it usually fires).
    const onPageHide = () => flushAll();
    window.addEventListener("pagehide", onPageHide);

    return () => {
      disposed = true;
      off();
      window.removeEventListener("pagehide", onPageHide);
      // Leaving the editor route (nav away) within the debounce window: flush the
      // pending saves instead of dropping them (bugloop L2 #1).
      flushAll();
      for (const { timer } of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, [store, slug, repo]);

  // Release the store on unmount (the provider won't, since it's caller-owned).
  useEffect(() => () => store.destroy(), [store]);

  return { store, ready, drafts };
}
